import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { confirmDelete } from '../../utils/confirm';

function YearModal({ tenantId, year, onClose, onSaved }) {
  const isEdit = !!year;
  const [form, setForm] = useState({
    yearName: year?.YearName || '',
    code: year?.Code || '',
    startDate: year?.StartDate ? year.StartDate.substring(0, 10) : '',
    endDate: year?.EndDate ? year.EndDate.substring(0, 10) : '',
    sequenceNo: year?.SequenceNo != null ? year.SequenceNo : '',
    isActive: year ? year.IsActive : true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.yearName.trim()) {
      toast.error('Academic Year Name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        tenantId,
        yearName: form.yearName.trim(),
        code: form.code.trim() || null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        sequenceNo: form.sequenceNo !== '' ? parseFloat(form.sequenceNo) : null,
        isActive: form.isActive,
      };

      if (isEdit) {
        await api.put(`/academic/years/${year.AcademicYearID}`, payload);
        toast.success('Academic Year updated');
      } else {
        await api.post('/academic/years', payload);
        toast.success('Academic Year created');
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Edit Academic Year' : 'Add Academic Year'}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="required-label">Academic Year Name</label>
            <input
              type="text"
              placeholder="e.g., 2025-26"
              value={form.yearName}
              onChange={(e) => setForm({ ...form, yearName: e.target.value })}
              autoFocus
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Code</label>
              <input
                type="text"
                placeholder="e.g., AY2526"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Sequence No</label>
              <input
                type="number"
                step="0.1"
                placeholder="e.g., 1 or 1.2"
                value={form.sequenceNo}
                onChange={(e) => setForm({ ...form, sequenceNo: e.target.value })}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Date</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm({ ...form, startDate: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm({ ...form, endDate: e.target.value })}
              />
            </div>
          </div>
          <div className="form-group">
            <label className="checkbox-inline">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              Is Active
            </label>
          </div>
          <div className="form-actions">
            <button type="button" className="btn btn-cancel-text" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={saving}>
              {saving ? 'Saving...' : isEdit ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AcademicYearsTab({ tenantId }) {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editYear, setEditYear] = useState(null);

  const loadYears = useCallback(async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      const res = await api.get(`/academic/years/${tenantId}`);
      if (res.data.success) setYears(res.data.data);
    } catch {
      toast.error('Failed to load academic years');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadYears(); }, [loadYears]);

  const handleDelete = async (yearId) => {
    if (!(await confirmDelete('Academic Year'))) return;
    try {
      await api.delete(`/academic/years/${yearId}`);
      toast.success('Academic Year deleted');
      loadYears();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <>
      <div className="page-card-header" style={{ margin: 0, paddingBottom: 16, borderBottom: '1px solid #e0e0e0' }}>
        <h3>Academic Years</h3>
        <button className="btn btn-add" onClick={() => { setEditYear(null); setShowModal(true); }}>
          + Add Academic Year
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>
      ) : years.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
          No academic years found. Click "Add Academic Year" to create one.
        </div>
      ) : (
        <table className="data-table" style={{ marginTop: 16 }}>
          <thead>
            <tr>
              <th>Seq</th>
              <th>Academic Year</th>
              <th>Code</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {years.map((y) => (
              <tr key={y.AcademicYearID}>
                <td style={{ color: '#616161', fontWeight: 600, width: 60 }}>{y.SequenceNo ?? '-'}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 18 }}>📅</span>
                    <strong>{y.YearName}</strong>
                  </div>
                </td>
                <td style={{ color: '#616161' }}>{y.Code || '-'}</td>
                <td>{formatDate(y.StartDate)}</td>
                <td>{formatDate(y.EndDate)}</td>
                <td>
                  <span className={`badge ${y.IsActive ? 'badge-active' : 'badge-inactive'}`}>
                    {y.IsActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  <div className="action-buttons">
                    <button className="action-btn action-edit" title="Edit" onClick={() => { setEditYear(y); setShowModal(true); }}>✏</button>
                    <button className="action-btn action-delete" title="Delete" onClick={() => handleDelete(y.AcademicYearID)}>🗑</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <YearModal
          tenantId={tenantId}
          year={editYear}
          onClose={() => { setShowModal(false); setEditYear(null); }}
          onSaved={() => { setShowModal(false); setEditYear(null); loadYears(); }}
        />
      )}
    </>
  );
}

export default AcademicYearsTab;
